/**
 * KISSED University · Admissions Quiz data + scoring + catalog mapping.
 *
 * Source of truth for:
 *  - The 5 quiz questions (`QUESTIONS`)
 *  - The 4 program cards (`PROGRAMS` — L1..L4 with hours/tuition/curriculum)
 *  - The CE course list (`CE_COURSES`)
 *  - The scoring engine (`computeRecommendation`)
 *  - The mapping from a quiz key → live catalog `primary_program` value
 *    (`QUIZ_TO_CATALOG_PRIMARY`, `mapCEFriendlyToCatalog`)
 *
 * The quiz NEVER writes its own program records — every recommendation is
 * resolved against the existing university catalog before submission.
 */

export const QUIZ_SESSION_KEY = "kissed_uni_quiz_v1";

export const QUESTIONS = [
  {
    id: "q1",
    text: "Do you have prior trichology training?",
    options: [
      { value: "none",         label: "No, I am brand new" },
      { value: "foundational", label: "Yes, I completed foundational training" },
      { value: "clinical",     label: "Yes, I have clinical experience" },
      { value: "advanced",     label: "I am an advanced practitioner" },
    ],
  },
  {
    id: "q2",
    text: "What are you looking for?",
    options: [
      { value: "foundation", label: "Foundational knowledge" },
      { value: "clinical",   label: "Hands-on clinical training" },
      { value: "complete",   label: "Complete practitioner training" },
      { value: "doctoral",   label: "Advanced doctoral pathway" },
      { value: "herbal",     label: "Herbal medicine" },
      { value: "ce",         label: "Continuing education" },
    ],
  },
  {
    id: "q3",
    text: "How do you prefer to learn?",
    options: [
      { value: "online",  label: "Online only" },
      { value: "hybrid",  label: "Hybrid with in-person labs" },
      { value: "handson", label: "Hands-on only" },
      { value: "unsure",  label: "Not sure yet" },
    ],
  },
  {
    id: "q4",
    text: "What is your primary goal?",
    options: [
      { value: "new_career",     label: "Start a new career" },
      { value: "add_to_career",  label: "Add trichology to my current beauty/wellness career" },
      { value: "clinic",         label: "Open or expand a clinic" },
      { value: "doctoral",       label: "Become an advanced educator or doctoral-level practitioner" },
      { value: "ce",             label: "Take a short CE course" },
    ],
  },
  {
    id: "q5",
    text: "When would you like to begin?",
    options: [
      { value: "June 2026",                  label: "June 2026" },
      { value: "July 2026",                  label: "July 2026" },
      { value: "August 2026",                label: "August 2026" },
      { value: "September 2026",             label: "September 2026" },
      { value: "I need admissions guidance", label: "I need admissions guidance" },
    ],
  },
];

export const PROGRAMS = {
  L1: {
    key: "L1",
    badge: "Level I — Foundational",
    level: "Level I",
    title: "Foundational Trichology Certification",
    college: "College of Trichology Science",
    hours: "300",
    tuition: "$2,497",
    perCourse: "$2,882",
    savings: "Save $385 paying in full",
    description:
      "Online foundational certification covering trichology science, hair biology, scalp science, herbal medicine, functional wellness, and client consultation.",
    forWhom: [
      "Students with no prior trichology training",
      "Beauty and wellness professionals expanding their expertise",
      "Anyone who wants a strong academic foundation before clinical training",
    ],
    format: "Online self-paced. All coursework delivered online. No in-person requirement for Level I.",
    courses: [
      { code: "TRI 101", title: "Introduction to Trichology",            hours: 30, price: "$297" },
      { code: "TRI 102", title: "History & Evolution of Trichology",     hours: 20, price: "$197" },
      { code: "TRI 103", title: "Anatomy & Physiology for Trichologists",hours: 40, price: "$447" },
      { code: "TRI 104", title: "Scalp Science & Disorders",             hours: 40, price: "$447" },
      { code: "TRI 105", title: "Hair Biology & Growth Cycles",          hours: 30, price: "$297" },
      { code: "TRI 106", title: "Psychology of Hair & Identity",         hours: 25, price: "$247" },
      { code: "TRI 107", title: "Foundations of Herbal Medicine",        hours: 35, price: "$347" },
      { code: "TRI 108", title: "A Gut Feeling: Digestive Wellness",     hours: 25, price: "$247" },
      { code: "TRI 109", title: "Functional Wellness Foundations",      hours: 20, price: "$197" },
      { code: "TRI 110", title: "Client Consultation & Intake",         hours: 15, price: "$147" },
      { code: "TRI 111", title: "Trichology Business Foundations",     hours: 10, price: "$97"  },
      { code: "TRI 112", title: "Foundational Case Studies",           hours: 10, price: "$97"  },
    ],
    grad: [
      "Complete all 12 Level I courses (300 clock hours)",
      "Pass all course assessments",
      "Submit TRI 112 Foundational Case Study capstone",
      "Complete TRI 110 consultation practicum documentation",
      "Pay all outstanding tuition",
      "Submit graduation application",
    ],
    displayCourseCount: 12,
  },
  L2: {
    key: "L2",
    badge: "Level II — Clinical Residency",
    level: "Level II",
    title: "Clinical Trichology Residency",
    college: "College of Trichology Science",
    hours: "600",
    tuition: "$3,497",
    perCourse: "$4,191",
    savings: "Save $694 paying in full",
    description:
      "Hands-on clinical training across four intensive residency blocks covering assessment, diagnostics, treatment modalities, advanced clinical practice, and supervised practicum.",
    forWhom: [
      "Students who completed Level I or equivalent foundational training",
      "Beauty professionals ready for advanced clinical skills",
      "Those seeking board certification eligibility",
    ],
    format: "Hybrid — online theory plus mandatory in-person clinical intensives at the KISSED University campus in North Carolina.",
    courses: [
      { code: "TRI 201", title: "Microscopic Hair & Scalp Analysis",      hours: 50, price: "$597" },
      { code: "TRI 202", title: "Clinical Consultation Techniques",      hours: 50, price: "$597" },
      { code: "TRI 203", title: "Hair Loss Classification & Assessment", hours: 50, price: "$597" },
      { code: "TRI 204", title: "Scalp Detoxification Protocols",        hours: 50, price: "$597" },
      { code: "TRI 205", title: "Low-Level Laser Therapy",               hours: 50, price: "$597" },
      { code: "TRI 206", title: "High Frequency & Advanced Therapies",   hours: 50, price: "$597" },
      { code: "TRI 207", title: "Herbal Treatment Planning",             hours: 50, price: "$597" },
      { code: "TRI 208", title: "Functional Wellness & Root Cause Analysis", hours: 50, price: "$597" },
      { code: "TRI 209", title: "Clinical Documentation & Progress Tracking", hours: 50, price: "$597" },
      { code: "TRI 210", title: "Clinical Practicum I",                  hours: 50, price: "$597" },
      { code: "TRI 211", title: "Clinical Practicum II",                 hours: 50, price: "$597" },
      { code: "TRI 212", title: "Comprehensive Case Presentations",      hours: 50, price: "$597" },
    ],
    grad: [
      "Complete Level I or documented equivalent (300 hours)",
      "Complete all 12 Level II courses (600 clock hours)",
      "Complete Clinical Practicum I and II under supervision",
      "Defend TRI 212 Comprehensive Case Presentation",
      "Board certification examination eligibility",
      "Pay all outstanding tuition",
      "Submit graduation application",
    ],
    displayCourseCount: 12,
  },
  L3: {
    key: "L3",
    badge: "Level III — Complete Practitioner",
    level: "Level III",
    title: "Complete Trichology Practitioner Pathway",
    college: "College of Trichology Science",
    hours: "900",
    tuition: "$4,960",
    perCourse: "$5,994",
    savings: "Save over $1,000 with the complete pathway",
    description:
      "The full practitioner pathway — Level I (300 hours) plus Level II (600 hours) — in one seamless program. Complete academic and clinical training for independent trichology practice.",
    forWhom: [
      "Students who want the complete foundation-to-clinic experience",
      "Those starting from zero who want full practitioner certification",
      "Anyone who wants both theory and hands-on clinical mastery",
    ],
    format: "Hybrid — Level I online self-paced, Level II includes mandatory in-person clinical intensives at the KISSED University campus in North Carolina.",
    courses: [
      { code: "TRI 101–112", title: "Level I — Foundational Certification (All 12 Courses)", hours: 300, price: "Included" },
      { code: "TRI 201–212", title: "Level II — Clinical Residency (All 12 Courses, 4 Blocks)", hours: 600, price: "Included" },
    ],
    grad: [
      "Complete all Level I coursework (300 hours)",
      "Complete all Level II residency blocks (600 hours)",
      "Complete Clinical Practicum I and II",
      "Defend Comprehensive Case Presentation",
      "Submit Foundational Case Study capstone",
      "Board certification eligibility",
      "Pay all outstanding tuition",
      "Submit graduation application",
    ],
    displayCourseCount: 24,
  },
  L4: {
    key: "L4",
    badge: "Level IV — Doctoral",
    level: "Level IV",
    title: "Doctor of Trichology Science (DTS)",
    college: "College of Trichology Science",
    hours: "1,200",
    tuition: "Admissions Review Required",
    perCourse: null,
    savings: null,
    description:
      "The highest credential at KISSED University. Six doctoral tracks covering advanced clinical reasoning, leadership and education, research and publication, advanced residency, doctoral capstone, and mentorship.",
    forWhom: [
      "Advanced practitioners and alumni",
      "Educators seeking doctoral credentials",
      "Clinical directors and practice leaders",
      "Those pursuing research, publication, or doctoral-level mastery",
    ],
    format: "Doctoral study may be completed fully remote or hybrid. Hybrid students may attend optional in-person doctoral intensives, clinical review sessions, or portfolio defense experiences when available. Requires admissions interview and portfolio review. Cohort-based.",
    courses: [
      { code: "TRI 401–403", title: "Track I — Advanced Clinical & Professional Practice", hours: 150, price: "By consultation" },
      { code: "TRI 404–406", title: "Track II — Leadership & Education",                  hours: 150, price: "By consultation" },
      { code: "TRI 407–409", title: "Track III — Research & Publication",                 hours: 150, price: "By consultation" },
      { code: "TRI 410–412", title: "Track IV — Advanced Residency",                      hours: 150, price: "By consultation" },
      { code: "TRI 413–415", title: "Track V — Doctoral Capstone",                        hours: 150, price: "By consultation" },
      { code: "TRI 416–418", title: "Track VI — Mentorship & Professional Contribution",  hours: 150, price: "By consultation" },
    ],
    grad: [
      "Complete Level III or documented equivalent (900 hours)",
      "Admissions interview and portfolio review prior to enrollment",
      "Complete all 6 doctoral tracks (300 additional hours)",
      "Complete Doctoral Clinical Residency I & II",
      "Defend Doctoral Portfolio before faculty panel",
      "Complete Professional Contribution Project",
      "Complete Mentorship Practicum",
      "Pay all outstanding tuition",
      "Submit doctoral graduation application",
    ],
    displayCourseCount: 18,
    slug: "dts",
  },
};

export const CE_COURSES = [
  { title: "Microscopic Hair & Scalp Analysis",            hours: "8 CE Hours", description: "Professional microscopy and trichoscopy for hair and scalp assessment in clinical practice." },
  { title: "Scalp Detoxification & Rejuvenation",          hours: "6 CE Hours", description: "Professional scalp detox protocols, deep cleansing, and follicle-clearing treatment application." },
  { title: "Low Level Laser Therapy",                       hours: "8 CE Hours", description: "Clinical LLLT for hair loss treatment — protocols, evidence base, contraindications, and device use." },
  { title: "High Frequency Therapy",                        hours: "6 CE Hours", description: "High-frequency modality application for scalp conditions, clinical indications, and protocols." },
  { title: "Herbal Foundations",                            hours: "8 CE Hours", description: "Introduction to herbal medicine for beauty and wellness professionals — key herbs and applications." },
  { title: "Herbal Infusion Scalp Therapy",                 hours: "6 CE Hours", description: "Designing and applying herbal infusion scalp treatments including formulation and client protocol." },
  { title: "Hair Tissue Mineral Analysis",                  hours: "8 CE Hours", description: "HTMA as a tool for assessing mineral imbalances related to hair loss and scalp health." },
  { title: "Functional Wellness for Beauty Professionals",  hours: "8 CE Hours", description: "Gut-hair connection, stress, hormones, sleep, and client education for holistic hair health." },
];

// L1..L4 → live catalog `primary_program` (round-trip-stable with options.program_options).
export const QUIZ_TO_CATALOG_PRIMARY = {
  L1: "Level I: Foundations of Trichology",
  L2: "Level II: Clinical Residency in Trichology",
  L3: "Level III: Professional Trichology Certification",
  L4: "Level IV: Doctor of Trichology Science (DTS) Pathway",
};

// Friendly CE name (from quiz) → closest catalog CE option. Falls back to
// "Other Continuing Education Courses" (a real catalog entry) when no exact
// match exists.
const CE_FRIENDLY_TO_CATALOG = {
  "Microscopic Hair & Scalp Analysis":            "Microscopic Hair & Scalp Analysis",
  "Scalp Detoxification & Rejuvenation":          "Scalp Detox & Rejuvenation",
  "Low Level Laser Therapy":                       "Low Level Laser Therapy",
  "High Frequency Therapy":                        "High Frequency Therapy",
  "Herbal Infusion Scalp Therapy":                 "Herbal Infusion Therapy",
  "Hair Tissue Mineral Analysis":                  "Hair Tissue Mineral Analysis (HTMA)",
  "Functional Wellness for Beauty Professionals":  "Functional Wellness Coaching",
  "Herbal Foundations":                            "Other Continuing Education Courses",
  "Not sure yet / Need guidance":                  "Other Continuing Education Courses",
};

export function mapCEFriendlyToCatalog(friendlyName) {
  if (!friendlyName) return "Other Continuing Education Courses";
  return CE_FRIENDLY_TO_CATALOG[friendlyName] || "Other Continuing Education Courses";
}

/**
 * Pure scoring engine. Returns one of: "L1" | "L2" | "L3" | "L4" | "HERBAL" | "CE".
 */
export function computeRecommendation(answers) {
  const a = answers || {};

  if (a.q2 === "herbal") return "HERBAL";
  if (a.q2 === "ce" || a.q4 === "ce") return "CE";

  const score = { L1: 0, L2: 0, L3: 0, L4: 0 };

  // Q1 — prior training
  if (a.q1 === "none")         { score.L1 += 5; score.L3 += 2; }
  if (a.q1 === "foundational") { score.L2 += 5; score.L3 += 3; }
  if (a.q1 === "clinical")     { score.L3 += 4; score.L4 += 3; }
  if (a.q1 === "advanced")     { score.L4 += 6; }

  // Q2 — what are you looking for
  if (a.q2 === "foundation") { score.L1 += 5; }
  if (a.q2 === "clinical")   { score.L2 += 5; }
  if (a.q2 === "complete")   { score.L3 += 6; }
  if (a.q2 === "doctoral")   { score.L4 += 6; }

  // Q3 — learning format
  if (a.q3 === "online")  { score.L1 += 3; }
  if (a.q3 === "hybrid")  { score.L2 += 2; score.L3 += 2; }
  if (a.q3 === "handson") { score.L2 += 3; }

  // Q4 — primary goal
  if (a.q4 === "new_career")    { score.L1 += 2; score.L3 += 2; }
  if (a.q4 === "add_to_career") { score.L1 += 3; }
  if (a.q4 === "clinic")        { score.L3 += 4; score.L2 += 2; }
  if (a.q4 === "doctoral")      { score.L4 += 6; }

  let best = "L1";
  let bestScore = -1;
  for (const key of ["L1", "L2", "L3", "L4"]) {
    if (score[key] > bestScore) {
      bestScore = score[key];
      best = key;
    }
  }

  // DTS eligibility guard: L4 (Doctor of Trichology Science) is NOT recommended
  // to applicants without clinical/advanced trichology preparation.
  // Beginners (q1=none) and foundational learners (q1=foundational) are redirected
  // to L3 (Holistic Trichology Practitioner Certification) regardless of score.
  if (best === "L4" && (a.q1 === "none" || a.q1 === "foundational")) {
    return "L3";
  }
  return best;
}

export function startMonthFromAnswer(q5Value) {
  // The quiz value IS the human-readable month string we want to persist.
  return q5Value || "";
}

export function persistQuizState(state) {
  try { sessionStorage.setItem(QUIZ_SESSION_KEY, JSON.stringify(state || {})); } catch { /* ignore */ }
}

export function readQuizState() {
  try {
    const raw = sessionStorage.getItem(QUIZ_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function clearQuizState() {
  try { sessionStorage.removeItem(QUIZ_SESSION_KEY); } catch { /* ignore */ }
}
